/**
 * Field Editing Indicator Component
 *
 * This component shows visual indicators for field-level collaborative editing
 * directly in the grid cells. It displays who is editing a field and provides
 * visual feedback about field editing status.
 */

import { useYJSCollaboration } from 'hooks/useYJSCollaboration';
import { Edit3, Lock } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { getOrCreateAnonymousUser } from 'utils/anonymousUserUtils';
import classes from './FieldEditingIndicator.module.scss';

export interface FieldEditingIndicatorProps {
  roomName: string;
  jobId: string;
  fieldName: string;
  fieldType: string;
  children: React.ReactElement;
  className?: string;
}

const FieldEditingIndicator: React.FC<FieldEditingIndicatorProps> = ({
  roomName,
  jobId,
  fieldName,
  fieldType,
  children,
  className
}) => {
  const {
    isFieldBeingEdited,
    getFieldEditors,
    startEditingField,
    stopEditingField,
    updateFieldValue,
    onFieldValueChange
  } = useYJSCollaboration();

  // Get current user name
  const currentUserName = getOrCreateAnonymousUser();

  // Local state to track if current user is editing this field
  const [isCurrentUserEditing, setIsCurrentUserEditing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [syncedFieldValue, setSyncedFieldValue] = useState<any>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get all editors for this field from WebSocket
  const allFieldEditors = getFieldEditors(roomName, fieldName, jobId);

  // Filter out the current user to get only other users editing this field
  const otherFieldEditors = allFieldEditors.filter(editor => editor.userName !== currentUserName);

  // Field is locked only if OTHER users are editing it (not the current user)
  // AND the current user is not currently editing it
  const isFieldLocked = otherFieldEditors.length > 0 && !isCurrentUserEditing;
  const fieldEditors = otherFieldEditors;

  // Update synced value from awareness data when other users are editing
  useEffect(() => {
    if (
      !isCurrentUserEditing &&
      otherFieldEditors.length > 0 &&
      otherFieldEditors[0].currentField?.fieldValue !== undefined
    ) {
      const awarenessValue = otherFieldEditors[0].currentField.fieldValue;
      if (syncedFieldValue !== awarenessValue) {
        setSyncedFieldValue(awarenessValue);
      }
    }
  }, [otherFieldEditors, isCurrentUserEditing, fieldName, syncedFieldValue]);

  // Clear synced value when no one is editing this field
  useEffect(() => {
    if (otherFieldEditors.length === 0 && !isCurrentUserEditing) {
      setSyncedFieldValue(null);
    }
  }, [otherFieldEditors.length, isCurrentUserEditing, fieldName]);

  // Force update DOM element when synced value changes to prevent parent override
  useEffect(() => {
    if (syncedFieldValue !== null && fieldRef.current) {
      // Try to find any input element and update it
      const inputElement = fieldRef.current.querySelector('input, select, textarea') as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement;
      if (inputElement) {
        // For date inputs, convert the value to the proper format
        if (inputElement.type === 'date' || inputElement.type === 'datetime-local') {
          let formattedValue = '';
          if (syncedFieldValue) {
            const date =
              syncedFieldValue instanceof Date ? syncedFieldValue : new Date(syncedFieldValue);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            if (inputElement.type === 'datetime-local') {
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              formattedValue = `${year}-${month}-${day}T${hours}:${minutes}`;
            } else {
              formattedValue = `${year}-${month}-${day}`;
            }
          }
          inputElement.value = formattedValue;
        } else {
          inputElement.value = syncedFieldValue;
        }

        // For dropdowns, also update selectedIndex
        if (inputElement.tagName === 'SELECT') {
          const selectElement = inputElement as HTMLSelectElement;
          const option = Array.from(selectElement.options).find(
            opt => opt.value === syncedFieldValue
          );
          if (option) {
            option.selected = true;
            selectElement.selectedIndex = option.index;
          }
          selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else {
        // For PrimeReact Calendar components, try to find the hidden input
        const calendarInput = fieldRef.current.querySelector(
          'input[type="hidden"]'
        ) as HTMLInputElement;
        if (calendarInput) {
          calendarInput.value = syncedFieldValue;
          calendarInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }, [syncedFieldValue, fieldName]);

  // Aggressive DOM update to prevent parent override - runs on every render
  useEffect(() => {
    if (syncedFieldValue !== null && fieldRef.current) {
      const updateDOM = () => {
        const inputElement = fieldRef.current?.querySelector('input, select, textarea') as
          | HTMLInputElement
          | HTMLSelectElement
          | HTMLTextAreaElement;
        if (inputElement) {
          // For date inputs, convert the value to the proper format
          if (inputElement.type === 'date' || inputElement.type === 'datetime-local') {
            let formattedValue = '';
            if (syncedFieldValue) {
              const date =
                syncedFieldValue instanceof Date ? syncedFieldValue : new Date(syncedFieldValue);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');

              if (inputElement.type === 'datetime-local') {
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                formattedValue = `${year}-${month}-${day}T${hours}:${minutes}`;
              } else {
                formattedValue = `${year}-${month}-${day}`;
              }
            }

            inputElement.value = formattedValue;
          } else {
            inputElement.value = syncedFieldValue;
          }

          // For dropdowns, also update selectedIndex
          if (inputElement.tagName === 'SELECT') {
            const selectElement = inputElement as HTMLSelectElement;
            const option = Array.from(selectElement.options).find(
              opt => opt.value === syncedFieldValue
            );
            if (option) {
              option.selected = true;
              selectElement.selectedIndex = option.index;
            }
          }
        } else {
          // For PrimeReact Calendar components, try to find the hidden input
          const calendarInput = fieldRef.current?.querySelector(
            'input[type="hidden"]'
          ) as HTMLInputElement;
          if (calendarInput && calendarInput.value !== syncedFieldValue) {
            calendarInput.value = syncedFieldValue;
          }
        }
      };

      // Update immediately
      updateDOM();

      // Also update after React's render cycle
      requestAnimationFrame(updateDOM);
    }
  }); // No dependency array - runs on every render

  // Click outside handler to stop editing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fieldRef.current && !fieldRef.current.contains(event.target as Node)) {
        if (isCurrentUserEditing) {
          setIsCurrentUserEditing(false);
          stopEditingField(roomName, jobId, fieldName);
        }
      }
    };

    if (isCurrentUserEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCurrentUserEditing, roomName, jobId, fieldName, stopEditingField]);

  // Cleanup effect to stop editing when component unmounts
  useEffect(() => {
    return () => {
      if (isCurrentUserEditing) {
        stopEditingField(roomName, jobId, fieldName);
      }

      // Clear any pending timeout
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [isCurrentUserEditing, roomName, jobId, fieldName, stopEditingField]);

  // Listen for field value changes from other users
  useEffect(() => {
    if (typeof onFieldValueChange !== 'function') {
      console.error(
        'FieldEditingIndicator: onFieldValueChange is not a function!',
        onFieldValueChange
      );
      return;
    }

    const unsubscribe = onFieldValueChange((data: any) => {
      // Only update if this is for the same field and job AND the current user is not editing this field
      if (
        data.roomName === roomName &&
        data.jobId === jobId &&
        data.fieldName === fieldName &&
        !isCurrentUserEditing
      ) {
        setSyncedFieldValue(data.fieldValue);
      }
    });

    return unsubscribe;
  }, [roomName, jobId, fieldName, onFieldValueChange, isCurrentUserEditing]);

  // Handle focus/blur for text inputs and date fields (primary method)
  const handleFocus = (e: any) => {
    setIsFocused(true);

    // Start editing on focus for text inputs, date fields, and dateTime fields (but not dropdowns)
    if (fieldType !== 'dropdown' && !isFieldLocked && !isCurrentUserEditing) {
      setIsCurrentUserEditing(true);
      setSyncedFieldValue(null); // Clear synced value when current user starts editing
      startEditingField(roomName, jobId, fieldName, fieldType);
    }

    // Call original onFocus if it exists
    children.props.onFocus?.(e);
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);

    // Stop editing on blur for text inputs, date fields, and dateTime fields (but not dropdowns)
    if (fieldType !== 'dropdown' && isCurrentUserEditing) {
      setIsCurrentUserEditing(false);
      stopEditingField(roomName, jobId, fieldName);
    }

    // Call original onBlur if it exists
    children.props.onBlur?.(e);
  };

  // Handle click for dropdowns (secondary method)
  const handleClick = (e: any) => {
    // Prevent interaction when locked
    if (isFieldLocked) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Start editing on click for dropdowns
    if (fieldType === 'dropdown' && !isCurrentUserEditing) {
      setIsCurrentUserEditing(true);
      startEditingField(roomName, jobId, fieldName, fieldType);
    }

    // Call original onClick if it exists
    children.props.onClick?.(e);
  };

  // Handle dropdown show/hide events
  const handleShow = (e: any) => {
    // Dropdown is opening, ensure we're in editing mode
    if (fieldType === 'dropdown' && !isCurrentUserEditing) {
      if (!isFieldLocked) {
        setIsCurrentUserEditing(true);
        setSyncedFieldValue(null); // Clear synced value when current user starts editing
        startEditingField(roomName, jobId, fieldName, fieldType);
      }
    }
    children.props.onShow?.(e);
  };

  const handleHide = (e: any) => {
    // Dropdown is closing, stop editing
    if (fieldType === 'dropdown' && isCurrentUserEditing) {
      setIsCurrentUserEditing(false);
      stopEditingField(roomName, jobId, fieldName);
    }
    children.props.onHide?.(e);
  };

  // Clone the child element and add event handlers
  const clonedChild = React.cloneElement(children, {
    onFocus: handleFocus,
    onBlur: handleBlur,
    onClick: handleClick,
    // Only add PrimeReact-specific events if the component supports them (not for native HTML elements)
    ...(fieldType === 'dropdown' &&
      children.type &&
      typeof children.type === 'object' &&
      'displayName' in children.type && {
        onShow: handleShow,
        onHide: handleHide
      }),
    onChange: (e: any) => {
      const value =
        e.target?.value !== undefined ? e.target.value : e.value !== undefined ? e.value : null;
      updateFieldValue(roomName, jobId, fieldName, value);
      // Call original onChange if it exists
      children.props.onChange?.(e);
    },
    // Sync the field value with other users' current values
    // When we have a synced value, make the field uncontrolled to prevent parent override
    ...(syncedFieldValue !== null
      ? {
          defaultValue:
            fieldType === 'date' || fieldType === 'dateTime'
              ? syncedFieldValue instanceof Date
                ? syncedFieldValue
                : new Date(syncedFieldValue)
              : syncedFieldValue
          // Don't pass value prop when synced - let it be uncontrolled
        }
      : {
          value: children.props.value
        }),
    // Don't disable fields - handle locking through visual indicators only
    disabled: children.props.disabled,
    // Apply visual styling for locked fields
    style: {
      ...children.props.style,
      opacity: isFieldLocked ? 0.6 : 1,
      cursor: isFieldLocked ? 'not-allowed' : children.props.style?.cursor || 'default',
      pointerEvents: isFieldLocked ? 'none' : 'auto'
    }
  });

  return (
    <div
      ref={fieldRef}
      className={`${classes.fieldEditingContainer} ${className || ''} ${isCurrentUserEditing ? classes.currentUserEditing : ''} ${isFieldLocked ? classes.fieldLocked : ''}`}
      style={{
        // Apply cell-level locking styles
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: '40px', // Ensure minimum cell height
        display: 'flex',
        alignItems: 'center',
        ...(isFieldLocked && {
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          border: '2px solid #ffc107',
          borderRadius: '4px',
          cursor: 'not-allowed',
          pointerEvents: 'none'
        }),
        ...(isCurrentUserEditing && {
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          border: '2px solid #28a745',
          borderRadius: '4px'
        })
      }}
    >
      {/* Cell content - only show if not locked */}
      {!isFieldLocked ? (
        <>
          {clonedChild}

          {/* Current user editing indicator */}
          {isCurrentUserEditing && (
            <div className={classes.currentUserIndicator}>
              <div className={classes.editingIcon}>
                <Edit3 size={10} />
              </div>
              <span className={classes.editingText}>Editing...</span>
            </div>
          )}
        </>
      ) : (
        /* Locked cell content */
        <div className={classes.lockedCellContent}>
          <div className={classes.lockIcon}>
            <Lock size={16} />
          </div>
          <div className={classes.editorInfo}>
            <div className={classes.lockedText}>Field is being edited by:</div>
            <div className={classes.editorList}>
              {fieldEditors.map(editor => (
                <div key={editor.userId} className={classes.editorBadge}>
                  <div className={classes.editorAvatar} style={{ backgroundColor: editor.color }}>
                    {editor.userName.charAt(0).toUpperCase()}
                  </div>
                  <span className={classes.editorName}>{editor.userName}</span>
                </div>
              ))}
            </div>
            {/* Show current value being edited */}
            {(() => {
              const shouldShowValue =
                fieldEditors.length > 0 &&
                ((fieldEditors[0].currentField?.fieldValue !== undefined &&
                  fieldEditors[0].currentField?.fieldValue !== null) ||
                  syncedFieldValue !== null);

              const displayValue =
                fieldEditors[0].currentField?.fieldValue !== undefined &&
                fieldEditors[0].currentField?.fieldValue !== null
                  ? fieldEditors[0].currentField.fieldValue
                  : syncedFieldValue;

              return (
                shouldShowValue && (
                  <div className={classes.currentValueDisplay}>
                    <div className={classes.currentValueLabel}>Current value:</div>
                    <div className={classes.currentValueText}>{displayValue}</div>
                  </div>
                )
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldEditingIndicator;
